import { Fragment, type ReactNode } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Title,
  Flex,
  Text,
  cn,
} from '@kognitos/lattice';
import { Link } from 'react-router-dom';

export interface IContentLayoutProps {
  breadcrumbs?: Array<{
    label: string;
    href?: string;
    icon?: ReactNode;
    dataTourId?: string;
  }>;
  title?: ReactNode;
  tag?: ReactNode;
  /**
   * Short uppercase label rendered above the title (in the breadcrumb slot),
   * styled with the lattice `Text` `eyebrow` variant.
   */
  eyebrow?: ReactNode;
  /** Applies a typography treatment to each breadcrumb label. */
  breadcrumbTextType?: 'default' | 'eyebrow';
  subtitle?: string;
  children?: ReactNode;
  extra?: ReactNode;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  extraClassName?: string;
  /**
   * Optional content to render between the header and main content area.
   * Typically used for banners or notifications that span the full width.
   */
  subHeader?: ReactNode;
  testId?: string;
}

function ContentLayout({
  breadcrumbs,
  title,
  tag,
  eyebrow,
  breadcrumbTextType = 'default',
  subtitle,
  children,
  extra,
  className,
  subHeader,
  contentClassName = 'container',
  headerClassName = 'container',
  extraClassName,
  testId,
}: IContentLayoutProps) {
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const hasTitle = !!title;
  const hasTitleSection = hasTitle || !!extra;
  const hasHeader = hasBreadcrumbs || hasTitleSection || !!eyebrow;

  return (
    <Flex
      data-testid={testId}
      direction="column"
      padding={8}
      gap={8}
      width="full"
      height="full"
      className={cn('pt-12', className)}
    >
      {hasHeader && (
        <Flex
          justify="between"
          align={'center'}
          className={cn('w-full mx-auto max-w-8xl', headerClassName)}
        >
          <Flex direction="column" gap={1}>
            {breadcrumbs && breadcrumbs.length > 0 && (
              <Breadcrumb data-testid="content-layout-breadcrumb">
                <BreadcrumbList>
                  {breadcrumbs.map((item, index) => (
                    <Fragment key={item.label}>
                      <BreadcrumbItem>
                        {item.href ? (
                          <BreadcrumbLink asChild>
                            <Link to={item.href} data-tour-id={item.dataTourId}>
                              <Flex align="center" gap={1}>
                                {item.icon}
                                {breadcrumbTextType === 'eyebrow' ? (
                                  <Text as="span" type="eyebrow">
                                    {item.label}
                                  </Text>
                                ) : (
                                  item.label
                                )}
                              </Flex>
                            </Link>
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage data-tour-id={item.dataTourId}>
                            <Flex align="center" gap={1}>
                              {item.icon}
                              {breadcrumbTextType === 'eyebrow' ? (
                                <Text as="span" type="eyebrow">
                                  {item.label}
                                </Text>
                              ) : (
                                item.label
                              )}
                            </Flex>
                          </BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                      {index < breadcrumbs.length - 1 && (
                        <BreadcrumbSeparator />
                      )}
                    </Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            )}

            {eyebrow && (
              <Text type="eyebrow" data-testid="content-layout-eyebrow">
                {eyebrow}
              </Text>
            )}

            {hasTitle && (
              <Flex direction="column" gap={1}>
                <Flex direction="row" gap={4} align="center">
                  <Title level="h3" data-testid="content-layout-title">
                    {title}
                  </Title>
                  {tag && <div data-testid="content-layout-tag">{tag}</div>}
                </Flex>
                {subtitle && (
                  <Text
                    color="muted"
                    level="small"
                    data-testid="content-layout-subtitle"
                  >
                    {subtitle}
                  </Text>
                )}
              </Flex>
            )}
          </Flex>

          {extra && (
            <Flex
              align="start"
              gap={3}
              data-testid="content-layout-extra"
              height="full"
              className={extraClassName}
            >
              {extra}
            </Flex>
          )}
        </Flex>
      )}

      {subHeader}

      {children && (
        <Flex
          className={cn('w-full flex-1 mx-auto max-w-8xl', contentClassName)}
          direction="column"
          data-testid="content-layout-children"
        >
          {children}
        </Flex>
      )}
    </Flex>
  );
}

export default ContentLayout;
